import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PerformanceSummary } from '@/hooks/usePartnerAnalytics';
import { RatingDisplay } from '@/components/partners/RatingDisplay';
import { PrequalificationStatusBadge } from '@/components/partners/PrequalificationStatusBadge';
import { Search } from 'lucide-react';
import { useState } from 'react';

interface ContractorListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractors: PerformanceSummary[];
}

export function ContractorListDialog({ open, onOpenChange, contractors }: ContractorListDialogProps) {
  const [search, setSearch] = useState('');

  const filtered = contractors.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>All Contractors ({contractors.length})</DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search contractors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="overflow-auto flex-1">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Documents</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((contractor) => (
                <TableRow key={contractor.id}>
                  <TableCell className="font-medium">{contractor.name}</TableCell>
                  <TableCell className="capitalize">{contractor.company_type}</TableCell>
                  <TableCell>
                    {contractor.overall_rating && (
                      <RatingDisplay rating={contractor.overall_rating} size="sm" />
                    )}
                  </TableCell>
                  <TableCell>
                    <PrequalificationStatusBadge status={contractor.prequalification_status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="text-sm">
                      <span className="text-green-600">{contractor.verified_documents}✓</span>
                      {contractor.pending_documents > 0 && (
                        <span className="text-yellow-600 ml-2">{contractor.pending_documents}⏳</span>
                      )}
                      {contractor.rejected_documents > 0 && (
                        <span className="text-red-600 ml-2">{contractor.rejected_documents}✗</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
